import React from "react";
import { Stack, Text } from "@mantine/core";
import type { DataTableColumn } from "@xplatform/design-system";
import { formatCount, formatDateTime, formatMoney, formatShortDateTime } from "@utils/monitoringFormat";
import { ActionsCell, EMPTY, StatusCell, TextCell, TwoLineCell, type RowAction } from "./cells";

/**
 * Column factories: the ONLY way admin tables declare columns. Each kind fixes
 * its width, alignment and cell, so the same kind of data looks the same in
 * every table (dates one line, numbers and money right-aligned, status badges
 * never squeezed, actions at the end). A text column without a width takes the
 * remaining space.
 */

export const COLUMN_WIDTH = {
  date: 136,
  number: 96,
  money: 128,
  status: 136,
  actions: 96,
  badge: 120,
} as const;

type Get<T, V> = (row: T) => V;

export const col = {
  text<T>(key: string, header: string, get: Get<T, React.ReactNode>,
          options: { width?: number | string; mono?: boolean; dimmed?: boolean; strong?: boolean } = {}): DataTableColumn<T> {
    return { key, header, width: options.width,
             render: (row) => <TextCell value={get(row)} mono={options.mono} dimmed={options.dimmed} strong={options.strong} /> };
  },

  twoLine<T>(key: string, header: string, get: Get<T, { primary: React.ReactNode; secondary?: React.ReactNode }>,
             options: { width?: number | string; mono?: boolean } = {}): DataTableColumn<T> {
    return { key, header, width: options.width, render: (row) => <TwoLineCell {...get(row)} mono={options.mono} /> };
  },

  /** A timestamp (ISO string or Date): `Oct 8, 23:21`, the full value in a tooltip. */
  date<T>(key: string, header: string, get: Get<T, string | Date | null | undefined>): DataTableColumn<T> {
    return {
      key, header, width: COLUMN_WIDTH.date,
      render: (row) => {
        const value = get(row);
        const iso = value instanceof Date ? value.toISOString() : value;
        return iso
          ? <TextCell value={formatShortDateTime(iso)} tooltip={formatDateTime(iso)} />
          : <TextCell value={EMPTY} dimmed />;
      },
    };
  },

  number<T>(key: string, header: string, get: Get<T, number | null | undefined>): DataTableColumn<T> {
    return { key, header, width: COLUMN_WIDTH.number, align: "right",
             render: (row) => <TextCell value={formatCount(get(row))} /> };
  },

  /** Money in its own currency, with an optional dimmed note line (e.g. "13 events"). */
  money<T>(key: string, header: string,
           get: Get<T, { amount: string | number | null | undefined; currency: string; note?: React.ReactNode; warn?: boolean }>):
    DataTableColumn<T> {
    return {
      key, header, width: COLUMN_WIDTH.money, align: "right",
      render: (row) => {
        const { amount, currency, note, warn } = get(row);
        return (
          <Stack gap={0} align="flex-end">
            <Text size="sm" fw={600}>{formatMoney(amount, currency)}</Text>
            {note && <Text size="xs" c={warn ? "orange.8" : "dimmed"} truncate>{note}</Text>}
          </Stack>
        );
      },
    };
  },

  status<T>(key: string, header: string, get: Get<T, string | null | undefined>): DataTableColumn<T> {
    return { key, header, width: COLUMN_WIDTH.status, render: (row) => <StatusCell status={get(row)} /> };
  },

  /** Any short badge-like content (a code, a capability). */
  badge<T>(key: string, header: string, render: (row: T) => React.ReactNode,
           width: number | string = COLUMN_WIDTH.badge): DataTableColumn<T> {
    return { key, header, width, render };
  },

  actions<T>(header: string, get: Get<T, RowAction[]>): DataTableColumn<T> {
    return { key: "actions", header, width: COLUMN_WIDTH.actions, align: "right",
             render: (row) => <ActionsCell actions={get(row)} /> };
  },
};
